import clsx from 'clsx';
import { Content } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack, StackProps, TooltipPopover } from 'src/core/ui';
import { SeatSaverIcon } from 'src/svgs';
import { AdminHoldIcon } from 'src/svgs/AdminHoldIcon';
import { ClockTrashIcon } from 'src/svgs/ClockTrashIcon';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';

import * as styles from './SeatingInfo.css';

export type SeatingInfoProps = {
  section: string;
  row: string | null;
  rowId?: number | null;
  seatFr: string | null;
  seatTo: string | null;
  isSeatSaver?: boolean;
  showSectionPrefix?: boolean;
  showSeatPrefix?: boolean;
  fontSize?: string;
  labelFontSize?: string;
  isDuplicate?: boolean;
  showAdminHold?: boolean;
  isAdminHold?: boolean;
  showOrgSeat?: boolean;
  orgSeatFr?: string | null;
  orgSeatTo?: string | null;
  isSoldOut?: boolean;
  justifyIconContent?: StackProps['justifyContent'];
  showUnmappedSection?: boolean;
  darkBackground?: boolean; // force dark-background
  lightBackground?: boolean; // force light-background
  isWasted?: boolean;
  overrideSectionRaw?: string | null;
  overrideRowRaw?: string | null;
  overrideSeatsRaw?: string | null;
  multiLineDisplay?: boolean;
  labelClassName?: string;
  singleLine?: boolean;
};

export const getSeatDisplayText = ({
  seatFr,
  seatTo,
  compact,
}: {
  seatFr: string | null | undefined;
  seatTo: string | null | undefined;
  compact?: boolean;
}) => {
  const hasSeats = !!(seatFr || seatTo);
  const isSingleSeat = hasSeats && (seatFr === seatTo || !seatTo);
  const isSeatRange = seatFr && seatTo && seatFr !== seatTo;

  let text = seatFr ?? '';
  if (isSeatRange) {
    text += compact ? '-' : ` - `;
  }
  if (!isSingleSeat) {
    text += seatTo ?? '';
  }

  return text;
};

// TODO: piggyback rows display?
export const SeatingInfo = ({
  seatFr,
  seatTo,
  section,
  row,
  rowId,
  isSeatSaver,
  showSectionPrefix,
  showSeatPrefix = true,
  fontSize,
  isDuplicate,
  showAdminHold,
  isAdminHold,
  showOrgSeat,
  orgSeatFr,
  orgSeatTo,
  isSoldOut,
  justifyIconContent,
  showUnmappedSection,
  darkBackground,
  lightBackground,
  isWasted,
  overrideSectionRaw,
  overrideRowRaw,
  overrideSeatsRaw,
  multiLineDisplay,
  labelFontSize,
  labelClassName,
  singleLine,
}: SeatingInfoProps) => {
  const hasSeats = !isSoldOut && !!(seatFr || seatTo);
  const hasOrgSeats = !!(orgSeatFr || orgSeatTo);
  const isSingleSeat = hasSeats && (seatFr === seatTo || !seatTo);
  const isOrgSingleSeat =
    hasOrgSeats && (orgSeatFr === orgSeatTo || !orgSeatTo);
  const showOrgSeatFr = showOrgSeat && orgSeatFr && seatFr !== orgSeatFr;
  const showOrgSeatTo =
    showOrgSeat && orgSeatTo && seatTo !== orgSeatTo && orgSeatTo !== seatFr;
  const isUnmappedSection = !rowId;

  const overrideSection =
    overrideSectionRaw && overrideSectionRaw?.trim() != ''
      ? overrideSectionRaw
      : null;
  const overrideRow =
    overrideRowRaw && overrideRowRaw?.trim() != '' ? overrideRowRaw : null;
  const overrideSeats =
    overrideSeatsRaw && overrideSeatsRaw?.trim() != ''
      ? overrideSeatsRaw
      : null;

  return (
    <Stack gap="m" alignItems="center" justifyContent={justifyIconContent}>
      <SeatingDisplay
        sectionDetails={
          <SectionDetails
            showSectionPrefix={!!showSectionPrefix}
            section={section}
            overrideSection={overrideSection}
            lightBackground={lightBackground}
            darkBackground={darkBackground}
          />
        }
        rowDetails={renderRowDetails({
          overrideRow: overrideRow,
          row: row,
          fontSize: labelFontSize,
          lightBackground,
          darkBackground,
          labelClassName,
        })}
        seatDetails={renderSeatDetails({
          isSoldOut: !!isSoldOut,
          overrideSeats,
          hasSeats,
          showSeatPrefix: !!showSeatPrefix,
          isSingleSeat,
          orgSeatFr,
          showOrgSeatFr: !!showOrgSeatFr,
          seatFr,
          seatTo,
          showOrgSeatTo: !!showOrgSeatTo,
          orgSeatTo,
          showOrgSeat: !!showOrgSeat,
          hasOrgSeats,
          isOrgSingleSeat,
          fontSize: labelFontSize,
          lightBackground,
          darkBackground,
          labelClassName,
        })}
        lightBackground={lightBackground}
        darkBackground={darkBackground}
        fontSize={fontSize}
        multiLineDisplay={multiLineDisplay}
        singleLine={singleLine}
      />
      <Stack gap="s">
        {isUnmappedSection && showUnmappedSection && (
          <TooltipPopover
            variant="link"
            contentVariant="dark"
            triggerContent={
              <WarningOutlineIcon fill={'#FD9927'} size={vars.iconSize.m} />
            }
          >
            <Content id={ContentId.SeatingInfoNotMatchStubHub} />
          </TooltipPopover>
        )}
        {isWasted && <ClockTrashIcon stroke={IconsFill.currentColor} />}
        {isSeatSaver && (
          <SeatSaverIcon fill={vars.color.textWarning} size={vars.iconSize.m} />
        )}
        {showAdminHold && isAdminHold && (
          <AdminHoldIcon
            fill={vars.color.textBrand}
            stroke={vars.color.backgroundPrimaryActive}
            size={vars.iconSize.m}
          />
        )}
        {isDuplicate && (
          <span className={styles.duplicateLabel}>
            <Content id={ContentId.Duplicate} />
          </span>
        )}
      </Stack>
    </Stack>
  );
};

const SeatingDisplay = ({
  sectionDetails,
  rowDetails,
  seatDetails,
  lightBackground,
  darkBackground,
  fontSize,
  multiLineDisplay,
  singleLine,
}: {
  sectionDetails?: React.ReactNode;
  rowDetails?: React.ReactNode;
  seatDetails?: React.ReactNode;
  lightBackground?: boolean;
  darkBackground?: boolean;
  fontSize?: string;
  multiLineDisplay?: SeatingInfoProps['multiLineDisplay'];
  singleLine?: boolean;
}) => {
  if (multiLineDisplay) {
    return (
      <Stack
        className={clsx(styles.noGapWrapper, {
          [styles.wrapperLight]: lightBackground,
          [styles.wrapperDark]: darkBackground,
          [styles.singleLineWrapper]: singleLine,
        })}
        style={{
          fontSize: fontSize,
          paddingTop: !rowDetails && !seatDetails ? vars.spacing.m : undefined,
        }}
        direction="column"
      >
        <div>{sectionDetails}</div>
        {(rowDetails || seatDetails) && (
          <Stack direction="row" gap="s" style={{ alignItems: 'baseline' }}>
            {rowDetails}
            {seatDetails}
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.wrapperLight]: lightBackground,
        [styles.wrapperDark]: darkBackground,
        [styles.singleLineWrapper]: singleLine,
      })}
      style={fontSize ? { fontSize: fontSize } : undefined}
    >
      {sectionDetails}
      {rowDetails}
      {seatDetails}
    </div>
  );
};

const SectionDetails = ({
  showSectionPrefix,
  section,
  overrideSection,
  lightBackground,
  darkBackground,
}: {
  showSectionPrefix: boolean;
  section: string;
  overrideSection: string | null;
  lightBackground?: boolean;
  darkBackground?: boolean;
}) => {
  return (
    <>
      {showSectionPrefix && (
        <div
          className={clsx(styles.attrLabel, {
            [styles.attrLabelLight]: lightBackground,
            [styles.attrLabelDark]: darkBackground,
          })}
        >
          <span>
            <Content id={ContentId.Section} />
          </span>
        </div>
      )}
      <span>{overrideSection ?? section}</span>
    </>
  );
};

const renderRowDetails = ({
  overrideRow,
  row,
  fontSize,
  lightBackground,
  darkBackground,
  labelClassName,
}: {
  overrideRow: string | null;
  row: string | null;
  fontSize?: string;
  lightBackground?: boolean;
  darkBackground?: boolean;
  labelClassName?: string; // TODO: fontSize and light/dark background should be replaced with this className prop
}) => {
  if (overrideRow || row) {
    return (
      <>
        <div
          className={clsx(styles.attrLabel, labelClassName, {
            [styles.attrLabelLight]: lightBackground,
            [styles.attrLabelDark]: darkBackground,
          })}
          style={fontSize ? { fontSize: fontSize } : undefined}
        >
          <span>
            <Content id={ContentId.Row} />
          </span>
        </div>
        <span>{overrideRow ?? row}</span>
      </>
    );
  }

  return null;
};

const renderSeatDetails = ({
  isSoldOut,
  overrideSeats,
  hasSeats,
  showSeatPrefix,
  isSingleSeat,
  orgSeatFr,
  showOrgSeatFr,
  seatFr,
  seatTo,
  showOrgSeatTo,
  orgSeatTo,
  showOrgSeat,
  hasOrgSeats,
  isOrgSingleSeat,
  fontSize,
  lightBackground,
  darkBackground,
  labelClassName,
}: {
  isSoldOut: boolean;
  overrideSeats: string | null;
  hasSeats: boolean;
  showSeatPrefix: boolean;
  isSingleSeat: boolean;
  orgSeatFr: string | null | undefined;
  showOrgSeatFr: boolean;
  seatFr: string | null;
  seatTo: string | null;
  showOrgSeatTo?: boolean;
  orgSeatTo: string | null | undefined;
  showOrgSeat?: boolean;
  hasOrgSeats: boolean;
  isOrgSingleSeat: boolean;
  fontSize?: string;
  lightBackground?: boolean;
  darkBackground?: boolean;
  labelClassName?: string; // TODO: fontSize and light/dark background should be replaced with this className prop
}) => {
  const attrLabelClass = clsx(styles.attrLabel, labelClassName, {
    [styles.attrLabelLight]: lightBackground,
    [styles.attrLabelDark]: darkBackground,
  });
  if (!isSoldOut) {
    if (overrideSeats || hasSeats) {
      return (
        <>
          {showSeatPrefix && (
            <div
              className={attrLabelClass}
              style={fontSize ? { fontSize: fontSize } : undefined}
            >
              <span>
                <Content id={isSingleSeat ? ContentId.Seat : ContentId.Seats} />
              </span>
            </div>
          )}
          {showOrgSeatFr && <span className={attrLabelClass}>{orgSeatFr}</span>}
          <span>{overrideSeats ?? getSeatDisplayText({ seatFr, seatTo })}</span>
          {showOrgSeatTo && <span className={attrLabelClass}>{orgSeatTo}</span>}
        </>
      );
    }
  } else {
    if (showOrgSeat && hasOrgSeats) {
      return (
        <>
          {showSeatPrefix && (
            <div
              className={attrLabelClass}
              style={fontSize ? { fontSize: fontSize } : undefined}
            >
              <Content
                id={isOrgSingleSeat ? ContentId.Seat : ContentId.Seats}
              />
            </div>
          )}
          <span>
            {getSeatDisplayText({ seatFr: orgSeatFr, seatTo: orgSeatTo })}
          </span>
        </>
      );
    }
  }

  return null;
};
